// client/src/components/order/PriceComparisonTable.js
import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const PriceComparisonTable = ({ items, priceChanges, className = '' }) => {
  // Create a map of price changes for quick lookup
  const priceChangeMap = {};
  priceChanges.forEach(change => {
    priceChangeMap[change.sku || change.name] = change;
  });

  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h4 className="font-semibold text-gray-900">Price Comparison</h4>
        <p className="text-xs text-gray-600 mt-1">
          Showing items with price changes since your last order
        </p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Item
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Previous Price
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Price
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Change
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Impact
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => {
              const change = priceChangeMap[item.sku || item.name];
              
              if (!item.priceChanged) {
                return (
                  <tr key={item.productId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.name}</p>
                        {item.sku && (
                          <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm text-gray-900">
                        €{item.originalPrice.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm text-gray-900">
                        €{item.currentPrice.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center">
                        <Minus className="w-4 h-4 text-gray-400" />
                        <span className="ml-2 text-sm text-gray-500">No change</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm text-gray-500">-</span>
                    </td>
                  </tr>
                );
              }

              const isIncrease = item.priceIncreased;
              const changeAmount = item.currentPrice - item.originalPrice;
              const totalImpact = changeAmount * item.quantity;

              return (
                <tr key={item.productId} className={`hover:bg-gray-50 ${
                  isIncrease ? 'bg-red-50' : 'bg-green-50'
                }`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.name}</p>
                      {item.sku && (
                        <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-gray-500 line-through">
                      €{item.originalPrice.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-semibold ${
                      isIncrease ? 'text-red-700' : 'text-green-700'
                    }`}>
                      €{item.currentPrice.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center">
                      {isIncrease ? (
                        <TrendingUp className="w-4 h-4 text-red-600" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-green-600" />
                      )}
                      <span className={`ml-2 text-sm font-medium ${
                        isIncrease ? 'text-red-700' : 'text-green-700'
                      }`}>
                        {isIncrease ? '+' : ''}{item.priceChangePercent}%
                        <span className="text-xs ml-1">
                          (€{Math.abs(changeAmount).toFixed(2)})
                        </span>
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-medium ${
                      isIncrease ? 'text-red-700' : 'text-green-700'
                    }`}>
                      {isIncrease ? '+' : '-'}€{Math.abs(totalImpact).toFixed(2)}
                    </span>
                    <p className="text-xs text-gray-500">
                      for {item.quantity} units
                    </p>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-100">
            <tr>
              <td colSpan="4" className="px-4 py-3 text-right font-semibold text-gray-900">
                Total Price Impact:
              </td>
              <td className="px-4 py-3 text-center">
                {(() => {
                  const totalChange = items.reduce((sum, item) => {
                    if (item.priceChanged) {
                      return sum + ((item.currentPrice - item.originalPrice) * item.quantity);
                    }
                    return sum;
                  }, 0);
                  
                  return (
                    <span className={`text-lg font-bold ${
                      totalChange > 0 ? 'text-red-700' : totalChange < 0 ? 'text-green-700' : 'text-gray-700'
                    }`}>
                      {totalChange > 0 ? '+' : ''}€{totalChange.toFixed(2)}
                    </span>
                  );
                })()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default PriceComparisonTable;